<template>
    <div v-if="match.placeholder" class="list-item thumbnail-canvas placeholder-container --placeholder" data-category="placeholder">
        <span class="placeholder-preview"></span>
        <span class="placeholder-label"></span>
    </div>

    <div v-else class="match-container" :data-index="match.id" :data-bin-id="match.bin_id" :class="{ selected: active }">
		<img :src="thumbnail" @load="revokeObjectURL(thumbnail)" @click="select(match)" :data-index="match.id" />
		<div class="label-container">
			<v-text-field
				v-if="renamingFile"
				@blur="endFileRenaming"
				@keydown="renameFile"
				v-model="renamingFileName"
				autofocus
				label="File Name"
				dark
				flat
				solo-inverted
				hide-details
			></v-text-field>
			<span v-else class="name">{{ nicename(match.name) }}</span>
		</div>

		<v-menu offset-y :content-class="'file-options-container'">
			<template v-slot:activator="{ on }">
				<i class="material-icons" v-on="on">more_vert</i>
			</template>
			<v-list>
				<v-list-item @click="downloadLUT(match)" :disabled="!canExportLUT || blockLoading">
                    <v-icon class="material-symbols-outlined">deployed_code_update</v-icon>
					<v-list-item-title>Download a LUT</v-list-item-title>
				</v-list-item><v-list-item @click="downloadXMP(match)" :disabled="!canExportXMP || blockLoading">
                	<img src="/assets/icons/lr-icon-96x96.png" />
					<v-list-item-title>Download XMP Profile</v-list-item-title>
				</v-list-item>
				<v-list-item @click="downloadCO(match)" :disabled="!canExportCO || blockLoading">
                	<img src="/assets/icons/co-icon-96x96.png" />
					<v-list-item-title>Download Capture One Style</v-list-item-title>
				</v-list-item>
				<v-list-item @click="downloadImage(match)" :disabled="!canExportLUT || blockLoading">
					<i class="material-icons">photo</i>
					<v-list-item-title>Save Image</v-list-item-title>
				</v-list-item>
				<v-list-item @click="applyGrade(match)" :disabled="blockLoading">
					<i class="material-icons">perm_media</i>
					<v-list-item-title>Apply Grade</v-list-item-title>
				</v-list-item>
                <v-list-item @click="AIColourMatchV2(match)" :disabled="!canUseColorGradingTools || !image || !imageHasIDT || blockLoading">
					<i class="material-icons">auto_awesome</i>
					<v-list-item-title>AI Colour Match</v-list-item-title>
				</v-list-item>
                <v-list-item @click="colourMatch(match)" :disabled="!canUseColorGradingTools || !image || !imageHasIDT || blockLoading">
					<i class="material-icons">filter</i>
					<v-list-item-title>Colour Match</v-list-item-title>
				</v-list-item>
				<v-list-item @click="startFileRenaming(match)" :disabled="!canRenameAsset">
					<i class="material-icons">create</i>
					<v-list-item-title>Rename</v-list-item-title>
				</v-list-item>
				<v-list-item @click="deleteMatch(match.id)" :disabled="!canDeleteAsset">
					<v-icon class="material-icons">mdi-delete</v-icon>
					<v-list-item-title>Delete</v-list-item-title>
				</v-list-item>
			</v-list>
		</v-menu>
	</div>
</template>

<script>
import download from 'downloadjs';
import { mapActions, mapGetters, mapState } from "vuex";

import CanMixin from "@/mixins/CanMixin";
import FilenameMixin from "@/mixins/Filename";

export default {
	mixins: [CanMixin, FilenameMixin],

	data() {
		return {
			renamingFile: false,
			renamingFileName: "",
			thumbnail: null,
		};
	},

	props: {
		match: Object,
	},

	created() {
        if (!this.match.placeholder) {
            this.thumbnail = URL.createObjectURL(this.match.blob);
        }
	},

	computed: {
	    ...mapGetters('Projects', ['assetById']),
        ...mapState('Projects', ['image']),
        ...mapState('App', ['blockLoading']),
        imageHasIDT() {
	        let file = this.assetById(this.image, 'image');

	        if (file?.IDT && !file.IDT?.initial) return true;
	        else return false;
        },
		active() {
			if (this.$store.state.Projects.match.id == this.match.id) return true;
			else return false;
		},
	},

	methods: {
		...mapActions("App", ["applyGrade"]),
		...mapActions("Helpers", ["revokeObjectURL"]),

		nicename(name) {
			return this.stripTimestampAndExtension(name);
		},

		select(match) {
			if (!this.$store.state.Projects.image) return;

			if (!window.lutify.splitViewActive || !this.active) {
				window.lutify.refreshSplitView(match);
			} else {
				window.lutify.removeSplitView();
			}
		},

		async deleteMatch(match_id) {
			// await this.$store.dispatch("Projects/selectBin", file.bin_id);
			let selectedFiles = [{ id: match_id }];

			this.$store.dispatch("Loader/showProgressBar", "indeterminate");

			for await (const selectedFile of selectedFiles) {
				let isUploading = await window.uploadMatchesStore.getItem(selectedFile.id);
				if (isUploading) {
					alert("Match can't be moved while uploading to server. Try again later");
					this.$store.dispatch("Loader/showProgressBar", false);
					return;
				}

				window.matchesStore.removeItem(selectedFile.id);
				await this.$store.dispatch("UI/removeMatch", selectedFile.id);
				// window.UI.setMatchesNumber(this.$store.getters["Projects/matchesNumber"] - 1);

				window
					.$(`#project-navigator-container .file[file_id="${selectedFile.id}"`)
					.closest(".v-treeview-node__root")
					.remove();
			}

			await this.$store.dispatch("Projects/moveTo", {
				file_id: selectedFiles[0].id,
				action: "Trash",
				file_ids: selectedFiles.map(e => e.id),
			});

			await this.$store.dispatch("Projects/refreshProjectTree");
			this.$store.dispatch("Loader/showProgressBar", false);
		},

		startFileRenaming(file) {
			this.$store.dispatch("Projects/setAction", {
				type: "file",
				action: "file-rename",
				data: file,
			});

			this.renamingFile = file.id;
			this.renamingFileName = this.stripTimestampAndExtension(file.name);
		},

		async renameFile(e) {
			if (e.key == "Enter" || e.keyCode == 13) {
				let oldFilename = this.$store.state.Projects.action.data.name;
				let fileType = oldFilename.substring(oldFilename.lastIndexOf(".") + 1);
				let newFilename = `${this.renamingFileName}.${Date.now()}.${fileType}`;

				this.renamingFile = false;

				if (this.stripTimestampAndExtension(oldFilename) !== this.renamingFileName) {
          await this.$store.dispatch("UI/renameMatch", {
            id: this.$store.state.Projects.action.data.id,
            name: newFilename,
            nicename: this.stripTimestampAndExtension(newFilename),
            bin_id: this.$store.state.Projects.action.data.bin_id
          });

					await this.$store.dispatch("Projects/updateFileName", {
						name: newFilename,
					});

					await this.$store.dispatch("Projects/refreshProjectTree", {
						createPresignedURL: true,
					});

					let matchesStoreData = await matchesStore.getItem(this.$store.state.Projects.action.data.id);
					if (matchesStoreData)
						matchesStore.setItem(matchesStoreData.id, { ...matchesStoreData, name: newFilename, nicename: this.renamingFileName });
				}

				this.endFileRenaming();
			} else if (e.key == "Escape" || e.keyCode == 27) {
				this.endFileRenaming();
			}
		},

		endFileRenaming() {
			this.renamingFile = false;

			this.$store.dispatch("Projects/setAction", {
				type: "file",
				action: "file-rename-end",
				data: null,
			});
		},

		async downloadLUT(match) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

			// let matchStoreData = await window.lutify.fetchMatch(vm.$store.getters["Projects/matchById"](match.id));
			let matchStoreData = await window.lutify.fetchMatch(match);

			await this.$store.dispatch("Projects/setAction", {
				type: "match",
				action: "export-lut-from-asset",
				data: [matchStoreData],
			});

            this.$store.dispatch('UI/setRightColumn', 'download-lut')
		},

        async downloadXMP(match) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

			let matchStoreData = await window.lutify.fetchMatch(match);

			await this.$store.dispatch("Projects/setAction", {
				type: "match",
				action: "export-xmp-from-asset",
				data: [matchStoreData],
			});

            this.$store.dispatch('UI/setRightColumn', 'download-xmp');
		},

        async downloadCO(match) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

            let matchStoreData = await window.lutify.fetchMatch(match);

            await this.$store.dispatch("Projects/setAction", {
                type: "match",
                action: "export-co-from-asset",
                data: [matchStoreData],
            });

            this.$store.dispatch('UI/setRightColumn', 'download-co');
        },

		async downloadImage(match) {
            if (lutify.isCurrentPlanFree()) {
                UI.showModal(UI.modals.notAvailableInFree.el);
                return;
            }

			// let matchStoreData = await window.lutify.fetchMatch(vm.$store.getters["Projects/matchById"](match.id));
			let matchStoreData = await window.lutify.fetchMatch(match);
			download(this.match.blob, `${matchStoreData.nicename}.jpg`, "image/jpeg");
		},

        async AIColourMatchV2(match) {
            if (! await _Adjustments.AIColourMatchV2.isModelDownloaded()) {
                _Adjustments.AIColourMatch.loadModel();
                return;
            }

            this.$store.dispatch("Loader/showProgressBar", "indeterminate");

            await window._Adjustments.AIColourMatchV2.addBlock();
            await window._Adjustments.AIColourMatchV2.setSampleFile(match);

            if (lutify.activeSidebarContainer !== "blocks") {
                this.$store.dispatch('UI/setLeftColumn', 'appliedBlocks')
                UI.scrollToBlock(lutify.activeBlock);
            }

            this.$store.dispatch("Loader/showProgressBar", false);
        },

        async colourMatch(match) {
            this.$store.dispatch("Loader/showProgressBar", "indeterminate");

            await window._Adjustments.ColourMatchV2.addBlock();
            UI.toggleStartTips()
            await window._Adjustments.ColourMatchV2.setSampleFile(match);
            await window._Adjustments.ColourMatchV2.apply();

            if (lutify.activeSidebarContainer !== "blocks") {
                this.$store.dispatch('UI/setLeftColumn', 'appliedBlocks')
                UI.scrollToBlock(lutify.activeBlock);
            }

            this.$store.dispatch("Loader/showProgressBar", false);
        },
	},
};
</script>

<style lang="scss">
.match-container {
	display: grid;
	grid-template-areas: "image image" "label delete-btn";
	grid-template-columns: 1fr 20px;
	grid-row-gap: 5px;
	align-items: center;
	align-content: center;
	&:not(:last-of-type) {
		margin-bottom: 20px;
	}
	> img {
        width: 100%;
        grid-area: image;
		cursor: pointer;
		border: 2px solid transparent;
		border-radius: var(--thumbnail-lg-border-radius);
		&:hover {
			border: 2px solid var(--primary);
		}
	}
	&.selected > img {
		border: 2px solid var(--primary);
	}

	> .label-container {
		display: flex;
		justify-content: space-between;
		align-items: center;
		grid-area: label;
		overflow: hidden;

		> .name {
			color: var(--font-default-color);
			font-size: 0.9rem;
			// padding-right: 25px;
			// text-transform: capitalize;
			text-align: left;
			line-height: 0.9rem;
			height: 100%;
			text-overflow: ellipsis;
			overflow: hidden;
			white-space: nowrap;
			margin-top: 2px;
			width: 87%;
		}

		> i.loading-spinner {
			margin-left: 10px;
			margin-right: 5px;
		}

		.v-input {
			font-size: 12px;
			.v-input__control {
				min-height: initial;
				// height: 10px;
				input {
					line-height: 1;
					color: var(--font-default-color) !important;
					padding: 0;
					text-overflow: ellipsis;
					overflow: hidden;
					white-space: nowrap;
				}

				.v-input__slot {
					padding: 0;
					background: transparent;
				}
			}

			&--is-focused {
				.v-input__slot {
					// background: #363636;
					background: transparent;
				}
			}
		}
	}

	i.material-icons {
		font-size: 18px;
		cursor: pointer;
	}
}
</style>
